import React, { useCallback, useEffect, useState } from "react";
import { Col, Row, Space, Table, Typography } from "antd";
import CustomBtn from "../../../../shared/CustomBtn/CustomBtn";
import { StakingResponse } from "@services/Funds.service";
import { FormikProps } from "formik";
import { transactionsColumns } from "./table-column";
import { ChevronDownIcon } from "./icons";
import { SorterResult } from "antd/lib/table/interface";

import * as style from "../StepsStyle.module.scss"

const TRANSACTIONS_AMOUNT_ON_FIRST_RENDER = 8;

type Props = {
  formik: FormikProps<any>;
  stakingTransactions: StakingResponse[];
  isUk?: boolean;
  onClickContinue: () => void;
  onClickCancel: () => void;
};

export const TransactionsStep = ({
  formik,
  stakingTransactions,
  isUk,
  onClickContinue,
  onClickCancel,
}: Props) => {
  const [displayedTransactions, setDisplayedTransactions] = useState<
    StakingResponse[]
  >([]);
  const [isCheckedAllTransactions, setIsCheckedAllTransactions] =
    useState(false);

  const handleCheckAllTransactions = useCallback(() => {
    if (stakingTransactions.length !== formik.values.transactionIds.length) {
      formik.setFieldValue(
        "transactionIds",
        stakingTransactions.map((x) => x.id)
      );
      setIsCheckedAllTransactions(true);
    } else {
      formik.setFieldValue("transactionIds", []);
      setIsCheckedAllTransactions(false);
    }
  }, [stakingTransactions, formik.values.transactionIds]);

  const handleClickShowAllTransactions = () => {
    if (displayedTransactions.length < stakingTransactions.length) {
      setDisplayedTransactions(stakingTransactions);
    }
  };

  const getRowClassName = (record: StakingResponse) => {
    if ((formik.values.transactionIds as number[]).includes(record.id)) {
      return style.selectedRow;
    }
  };

  const handleCheckTransaction = (record: StakingResponse) => {
    const { transactionIds } = formik.values;
    const isChecked = transactionIds.includes(record.id);

    if (isChecked) {
      formik.setFieldValue(
        "transactionIds",
        transactionIds.filter((x: number) => x !== record.id)
      );
    } else {
      formik.setFieldValue("transactionIds", [...transactionIds, record.id]);
    }
  };

  const handleSortChange = (
    _: any,
    _2: any,
    sorter: SorterResult<StakingResponse> | SorterResult<StakingResponse>[]
  ) => {
    const sortArray: SorterResult<StakingResponse>[] = Array.isArray(sorter)
      ? sorter
      : [sorter];
    const sortedData = [...stakingTransactions].sort((a, b) => {
      const currentSorter = sortArray[0];

      if (!currentSorter.field || !currentSorter.order) return 0;

      const isNumeric = currentSorter.field === "amount";
      const isDate =
        new Date(a[currentSorter.field as keyof StakingResponse]).toString() !==
        "Invalid Date";

      if (isNumeric) {
        return currentSorter.order === "ascend"
          ? Number(a[currentSorter.field as keyof StakingResponse]) -
          Number(b[currentSorter.field as keyof StakingResponse])
          : Number(b[currentSorter.field as keyof StakingResponse]) -
          Number(a[currentSorter.field as keyof StakingResponse]);
      } else if (isDate) {
        const dateA = new Date(
          a[currentSorter.field as keyof StakingResponse]
        ).getTime();
        const dateB = new Date(
          b[currentSorter.field as keyof StakingResponse]
        ).getTime();
        return currentSorter.order === "ascend" ? dateB - dateA : dateA - dateB;
      } else {
        return currentSorter.order === "ascend"
          ? (
            a[currentSorter.field as keyof StakingResponse] as string
          ).localeCompare(
            b[currentSorter.field as keyof StakingResponse] as string
          )
          : (
            b[currentSorter.field as keyof StakingResponse] as string
          ).localeCompare(
            a[currentSorter.field as keyof StakingResponse] as string
          );
      }
    });
    setDisplayedTransactions(sortedData.slice(0, 8));
  };

  useEffect(() => {
    if (
      isCheckedAllTransactions &&
      stakingTransactions.length !== formik.values.transactionIds.length
    ) {
      setIsCheckedAllTransactions(false);
    } else if (
      !isCheckedAllTransactions &&
      stakingTransactions.length === formik.values.transactionIds.length
    ) {
      setIsCheckedAllTransactions(true);
    }
  }, [formik.values.transactionIds, stakingTransactions.length]);

  useEffect(() => {
    const mutableStakingTransactions = [...stakingTransactions];
    setDisplayedTransactions(
      mutableStakingTransactions.splice(0, TRANSACTIONS_AMOUNT_ON_FIRST_RENDER)
    );
  }, [stakingTransactions]);

  return (
    <>
      <Row>
        <Col className={style.mainLabel}>
          <Typography.Title level={5}>Transactions</Typography.Title>
          {stakingTransactions.length == 0 && (
            <p>No transactions available to timelock found</p>
          )}
          {stakingTransactions.length > 0 && (
            <p>Select transactions to timelock</p>
          )}
        </Col>
      </Row>

      {stakingTransactions.length > 0 && (
        <Table
          rowKey={"id"}
          rowClassName={getRowClassName}
          onChange={handleSortChange}
          columns={transactionsColumns({
            formik,
            isCheckedAll: isCheckedAllTransactions,
            handleCheckAllTransactions,
          })}
          dataSource={displayedTransactions}
          className={style.table}
          onRow={(record) => ({
            onClick: () => handleCheckTransaction(record),
          })}
          pagination={false}
        />
      )}

      {displayedTransactions.length < stakingTransactions.length && (
        <Space direction="horizontal" className={style.showAllResultsBtn}>
          <CustomBtn myType="cancel" onClick={handleClickShowAllTransactions}>
            Show all results <ChevronDownIcon />
          </CustomBtn>
        </Space>
      )}

      <Space direction="horizontal" className={style.footer}>
        <CustomBtn
          myType="cancel"
          onClick={onClickCancel}
          className={style.button}
        >
          Cancel
        </CustomBtn>

        <CustomBtn
          myType="submit"
          disabled={!formik.values.transactionIds.length}
          onClick={onClickContinue}
          className={style.button}
        >
          Continue
        </CustomBtn>
      </Space>
    </>
  );
};
